import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Baby, ChevronRight, BookOpen, Users, Heart, Home, TreeDeciduous } from 'lucide-react';

const FAMILY_CONTENT = [
  {
    id: 'origin',
    title: 'Din ursprungsfamilj',
    icon: TreeDeciduous,
    description: 'Förstå hur din uppväxt påverkar dig idag.',
    content: [
      '**Vi formas av vår uppväxt:** Dina första relationer – med föräldrar och syskon – skapar mallar för hur du fungerar i relationer som vuxen.',
      '**Anknytning:** Hur dina föräldrar svarade på dina behov som barn påverkar din trygghet i relationer.',
      '**Mönster går i arv:** Sätt att kommunicera, hantera konflikter och visa kärlek förs ofta vidare.',
      '**Medvetenhet är nyckeln:** Genom att förstå varifrån dina mönster kommer kan du välja att behålla det som fungerar och förändra det som inte gör det.',
      '**Det handlar inte om skuld:** Att förstå din uppväxt är inte att skylla på dina föräldrar – de gjorde sitt bästa med sina förutsättningar.'
    ],
    questions: [
      'Hur visades kärlek i din familj?',
      'Hur hanterades konflikter när du växte upp?',
      'Vilka roller hade familjemedlemmarna?',
      'Vad är du tacksam för från din uppväxt?',
      'Vad vill du göra annorlunda i dina egna relationer?'
    ]
  },
  {
    id: 'parenting',
    title: 'Föräldraskap',
    icon: Baby,
    description: 'Tankar och verktyg för dig som är förälder.',
    content: [
      '**Barn behöver trygghet:** En trygg bas att utgå från och återvända till.',
      '**Var närvarande:** Kvalitetstid handlar om uppmärksamhet, inte aktiviteter.',
      '**Validera känslor:** "Jag förstår att du är ledsen" – även om du sätter gränser.',
      '**Modellera beteende:** Barn gör som du gör, inte som du säger.',
      '**Ta hand om dig själv:** Du kan inte ge från en tom kopp.',
      '**Perfekt föräldraskap finns inte:** "Good enough" är verkligen tillräckligt.',
      '**Reparera:** Det viktiga är inte att aldrig göra fel, utan att kunna reparera efteråt.'
    ],
    questions: [
      'Vilken förälder vill du vara?',
      'Vad tar du med dig från din egen uppväxt?',
      'Vad vill du göra annorlunda?',
      'Hur tar du hand om dig själv som förälder?',
      'Vad är du stolt över i ditt föräldraskap?'
    ]
  },
  {
    id: 'siblings',
    title: 'Syskonrelationer',
    icon: Users,
    description: 'Förstå dynamiken mellan syskon.',
    content: [
      '**Syskon är våra första kamrater:** Här lär vi oss dela, kompromissa och konkurrera.',
      '**Ordning spelar roll:** Förstfödda, mellanbarn och yngst har ofta olika roller.',
      '**Favoritisering händer:** Föräldrar försöker vara rättvisa, men barn kan uppfatta det olika.',
      '**Vuxna syskon:** Relationen förändras när ni blir vuxna – den måste omförhandlas.',
      '**Konflikter kan läka:** Det är aldrig för sent att reparera en syskonrelation.',
      '**Olika perspektiv:** Ni kan ha helt olika minnen av samma uppväxt – båda kan vara sanna.'
    ],
    questions: [
      'Hur var din relation till dina syskon som barn?',
      'Hur är relationen idag?',
      'Vilken roll hade du bland syskonen?',
      'Finns det något olöst mellan er?',
      'Vad uppskattar du hos dina syskon?'
    ]
  },
  {
    id: 'boundaries-family',
    title: 'Gränser mot familj',
    icon: Home,
    description: 'Att sätta hälsosamma gränser med ursprungsfamiljen.',
    content: [
      '**Gränser är kärleksfulla:** Att sätta gränser betyder inte att du inte älskar din familj.',
      '**Vuxna barn, vuxna relationer:** Din relation till dina föräldrar behöver utvecklas när du blir vuxen.',
      '**Din nya familj prioriteras:** Om du har partner/barn har de företräde – det är naturligt och hälsosamt.',
      '**Du har rätt att säga nej:** Även till jul, familjemiddagar eller att berätta allt.',
      '**Förvänta dig motstånd:** Familjer vill behålla status quo. Stå fast vid dina gränser.',
      '**Begränsa tid om det behövs:** Du behöver inte spendera mer tid med familj än vad som är bra för dig.'
    ],
    questions: [
      'Vilka gränser behöver du sätta mot din familj?',
      'Hur reagerar din familj när du sätter gränser?',
      'Känner du skuld när du prioriterar din egen familj?',
      'Vad hade du behövt höra som barn som du kan säga till dig själv nu?'
    ]
  },
  {
    id: 'healing',
    title: 'Läka familjsår',
    icon: Heart,
    description: 'Bearbeta svåra upplevelser från uppväxten.',
    content: [
      '**Erkänn din smärta:** Det första steget är att erkänna att du blev sårad.',
      '**Sörj det du inte fick:** Det är okej att sörja den barndom du inte hade.',
      '**Förlåtelse är för dig:** Att förlåta betyder inte att acceptera eller glömma – det är för din egen skull.',
      '**Du kan inte ändra dem:** Du kan bara ändra hur du förhåller dig till dem.',
      '**Sök professionell hjälp:** Djupa sår kan behöva bearbetas med en terapeut.',
      '**Du är inte din historia:** Det som hände dig definierar inte vem du är eller kan bli.',
      '**Bryt cykeln:** Du kan välja att inte föra destruktiva mönster vidare.'
    ],
    questions: [
      'Vilka sår bär du från din uppväxt?',
      'Vad behöver du sörja?',
      'Finns det någon du behöver förlåta – eller be om förlåtelse av?',
      'Hur tar det gamla livet plats i ditt nuvarande liv?',
      'Vad vill du bryta mönstret av?'
    ]
  }
];

export default function FamilyPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  const [selectedTopic, setSelectedTopic] = useState(null);

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="family-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/relations')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Familj</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att få tillgång till familjeverktyg.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  if (selectedTopic) {
    const topic = FAMILY_CONTENT.find(t => t.id === selectedTopic);
    return (
      <div className="min-h-screen bg-background" data-testid="family-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => setSelectedTopic(null)} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Familj</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          <div className="bg-rose-50 rounded-2xl p-8">
            <topic.icon className="w-10 h-10 text-rose-600 mb-4" />
            <h2 className="font-heading text-2xl font-semibold mb-2">{topic.title}</h2>
            <p className="text-muted-foreground">{topic.description}</p>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
            {topic.content.map((text, i) => (
              <p key={i} className="text-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
            ))}
          </div>

          <div className="bg-primary/5 rounded-2xl p-6 space-y-4">
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              Reflektionsfrågor
            </h3>
            {topic.questions.map((q, i) => (
              <div key={i} className="flex gap-3 items-start p-4 rounded-xl bg-white">
                <span className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium text-primary shrink-0">{i + 1}</span>
                <span>{q}</span>
              </div>
            ))}
          </div>

          {/* Contact */}
          
          <p className="text-xs text-muted-foreground text-center px-4">
            ⚕️ Informationen här ersätter inte professionell terapi. Sök hjälp om du behöver bearbeta traumatiska upplevelser.
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="family-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/relations')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-light">Familj</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="text-center space-y-3">
          <Baby className="w-12 h-12 mx-auto text-rose-500" />
          <h2 className="font-heading text-2xl font-semibold">Familj</h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Utforska din ursprungsfamilj, föräldraskap och familjedynamik.
          </p>
        </div>

        <div className="space-y-3">
          {FAMILY_CONTENT.map((topic) => (
            <button
              key={topic.id}
              onClick={() => setSelectedTopic(topic.id)}
              className="w-full bg-white p-5 rounded-2xl border border-border/30 text-left hover:shadow-md transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center">
                  <topic.icon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{topic.title}</h3>
                  <p className="text-sm text-muted-foreground">{topic.description}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
              </div>
            </button>
          ))}
        </div>

        {/* Quick tip */}
        <div className="bg-rose-50 rounded-2xl p-4 border border-rose-100">
          <p className="text-rose-800 text-sm">
            💡 <strong>Kom ihåg:</strong> Att förstå din historia ger dig makt att skriva framtiden.
          </p>
        </div>

        {/* Contact */}
      </main>
    </div>
  );
}
